import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Link } from 'react-router-dom';
import { LazyImage } from '../../components/LazyImage';
import { FullScreenImagePreviewTrigger } from '../../components/FullScreenImagePreview';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';
import { usePagePreviewHover } from '../../components/pagePreview/usePagePreviewHover';
import type { ClusterResponse } from './insights';

export function MembersGrid({
  pages,
  categoryName,
  selectedRows,
  toggle,
  memberIds,
}: {
  pages: ClusterResponse['pages'];
  categoryName: string | undefined;
  selectedRows: Set<string>;
  toggle: (id: string) => void;
  memberIds: string[];
}) {
  const { show, hide } = usePagePreviewHover();

  return (
    <div className="@container">
      <div className="grid grid-cols-2 gap-4 @md:grid-cols-4 @xl:grid-cols-6">
        {pages.map((page) => {
          const selected = selectedRows.has(page.id);
          return (
            <div
              key={page.id}
              className={clsx(
                'rounded-lg border p-1.5 transition-colors',
                selected ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40' : 'border-transparent',
              )}
              onMouseEnter={(e) => show(page, e.currentTarget.getBoundingClientRect())}
              onMouseLeave={hide}
            >
              <div className="relative">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggle(page.id)}
                  className="absolute left-1 top-1 z-10 size-4"
                  aria-label={`Select page ${page.id}`}
                />
                {page.isOutlier && (
                  <span className="absolute right-1 top-1 z-10 rounded bg-red-500 px-1 text-[10px] font-bold text-white">
                    outlier
                  </span>
                )}
                <div className="group relative aspect-3/4">
                  <LazyImage src={pagePreviewSrc(page.id, { resize: 'x700' })} className="h-full w-full" />
                  <FullScreenImagePreviewTrigger pageId={page.id} pageIds={memberIds} />
                </div>
              </div>
              <Link
                to={`/document-pages/${page.id}`}
                className={twMerge(
                  clsx('mt-1 block truncate text-center text-xs font-bold text-gray-500 dark:text-zinc-400', {
                    'text-red-500 dark:text-red-400': page.classificationCategory !== categoryName,
                  }),
                )}
              >
                {page.classificationCategory}
              </Link>
              <div className="truncate text-center text-xs text-gray-500 dark:text-zinc-400">
                {(page.classificationScore * 100).toFixed(0)}% · sim {(page.similarityToCentroid * 100).toFixed(0)}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
